/**
 * K4: seam gates (docs/design/2026-10-11-open-world-plan.md). Entering a gate (`enter`) starts a ride of 2-4 s during
 * which every command is refused; at the end the player is sent to the paired gate's region through the World's own
 * zone transfer (the one warps and carriages use). The mount ridden is kept: a scene mark carries it over and the next
 * World puts it back in `setup`. Time only from `step(dt)`; no randomness.
 */
import { SEAM_GATES, PLACES, REGIONS, type RegionId, type SeamGate } from '../data/regions';
import { zoneEnabled } from '../data/enabledZones';
import type { ZoneId } from '../data/zones';
import type { SystemHost, WorldSystem } from './types';

/** Ride seconds by kind (foot is the short rope-bridge walk). */
export const SEAM_RIDE_SECONDS: Readonly<Record<SeamGate['ride'], number>> = { foot: 2, carriage: 3, boat: 4 };

export const SEAM_MARK = 'seam-arrive:';

export interface SeamRide {
  readonly gate: SeamGate['id'];
  /** The end entered from, and the region the ride ends in. */
  readonly from: 'a' | 'b';
  readonly toRegion: RegionId;
  readonly toName: string;
  readonly dur: number;
  t: number;
  readonly mount: string | null;
}

/** The first enabled zone of a region: where a ride into it lands. */
export const regionLanding = (r: RegionId): ZoneId | null => PLACES.find((p) => p.region === r && zoneEnabled(p.zone))?.zone ?? null;

export class SeamGateSystem implements WorldSystem {
  readonly name = 'seamGate';
  ride: SeamRide | null = null;
  constructor(private readonly h: SystemHost) {}

  /** Starts the ride through a gate from the given end; false when busy or the far region has no open zone. */
  enter(id: SeamGate['id'], from: 'a' | 'b'): boolean {
    if (this.ride) return false;
    const g = SEAM_GATES.find((x) => x.id === id);
    if (!g) return false;
    const toRegion = (from === 'a' ? g.b : g.a).region;
    if (!regionLanding(toRegion)) return false;
    const w = this.h.world;
    Object.assign(this.h.input, { sprint: false, engage: false, talkTo: null });
    this.ride = { gate: id, from, toRegion, toName: REGIONS[toRegion].name, dur: SEAM_RIDE_SECONDS[g.ride], t: 0, mount: w.player.mount };
    return true;
  }

  /** While riding nothing else may be done. */
  refuse(): boolean {
    return !!this.ride;
  }

  step(dt: number): void {
    const r = this.ride;
    if (!r) return;
    r.t += dt;
    if (r.t < r.dur) return;
    this.ride = null;
    const to = regionLanding(r.toRegion)!;
    const pr = this.h.world.progress;
    pr.scenes = pr.scenes.filter((s) => !s.startsWith(SEAM_MARK));
    pr.scenes.push(`${SEAM_MARK}${r.gate}:${r.from === 'a' ? 'b' : 'a'}:${r.mount ?? ''}`);
    (this.h.world as unknown as { travel(to: ZoneId, byPortal: boolean): void }).travel(to, false);
  }

  /** Arrived through a gate: put the mount back and drop the mark. */
  setup(): void {
    const w = this.h.world;
    const pr = w.progress;
    const mark = pr.scenes.find((s) => s.startsWith(SEAM_MARK));
    if (!mark) return;
    pr.scenes = pr.scenes.filter((s) => s !== mark);
    const mount = mark.split(':')[3];
    if (mount && pr.mounts.includes(mount)) {
      w.player.mount = mount;
      this.h.emit({ type: 'mount', id: w.player.id, mount });
    }
  }
}
